import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { backgroundChatKeys, chatKeys } from '~/lib/query-keys';
import { useSession } from '~/lib/stores/session-store';
import { timezoneHeader } from '~/lib/timezone';
import {
  buildRealtimeEndedRequest,
  buildRealtimeUsageRequest,
  realtimeUsageReportOf,
  usageCallIdOf,
} from './realtime-usage';
import { routeVoiceEvent, type VoiceStatus } from './voice-event-router';
import { VoiceTurnGate } from './voice-turn-gate';
import { LiveCallTracker, type LiveDelegateRequestBody, type LiveTrackerEffect } from './live-call-tracker';
import { lateResultSignalAction, subscribeLiveLateResult } from './live-late-result-signal';

export type VoiceCallState =
  | 'idle'
  | 'connecting'
  | 'live'
  | 'ending'
  | 'error';

/** A single spoken turn captured during a call, in call order. */
export interface VoiceTurn {
  role: 'user' | 'assistant';
  content: string;
}

interface UseVoiceCallOptions {
  /**
   * Active chat branch. Voice turns are persisted with this branch id so
   * they appear in the same thread as the surrounding text conversation.
   */
  branchId: string;
  /**
   * FRONT-531: bind the call to a specific chat session instead of the global
   * canonical one. The zen chat modal passes its own `web:<uuid>` thread id so
   * the call's turns, recap, and save proposal land in THAT thread (and the
   * modal's history refetch shows them) — not orphaned in the global session.
   * Falls back to the global session when omitted (the legacy home behavior).
   */
  sessionId?: string;
  /**
   * FRONT-570: the content of the file/document this chat is anchored to, when
   * there is one. Sent to the session mint so the voice agent already knows the
   * asset — mirrors the `assetContext` the typed chat injects on every turn.
   * The chat's conversation so far is loaded server-side from `sessionId`, so
   * only the asset content needs to ride along from the client.
   */
  assetContext?: string;
  /**
   * FRONT-1336: the Work container this chat is pinned to, when there is one.
   * The server normally reads the scope back from the session id — but a chat
   * minted by the Work board's "Ask Eddy about this" only persists its scope
   * on the first TYPED turn, and pressing the phone button straight away is
   * exactly the hands-free case this feature is for. Sending it from here
   * covers that turn-one race; the server re-resolves the id against the
   * caller's own org and user, so a forged one resolves to nothing.
   */
  scopeEntityId?: string;
  /**
   * Called every time the assistant transcript receives a new delta. The
   * surface uses this to stream Eddy's reply into a live chat bubble in
   * real time, before the row is persisted to chat_messages.
   */
  onAssistantDelta?: (delta: string) => void;
  /**
   * Called when the assistant turn finishes (after all deltas + the final
   * `…transcript.done` event). The hook will also have already POSTed the
   * row to /persist by the time this fires.
   */
  onAssistantFinal?: (text: string) => void;
  /**
   * Called when the user transcript arrives (one event per user turn).
   */
  onUserTranscript?: (text: string) => void;
  /**
   * Called after a call ends AND at least one turn (user or assistant) was
   * exchanged during the call. Receives the full list of turns captured
   * during the call, in order, so the caller can hand the transcript to the
   * end-of-call endpoint directly instead of racing the per-turn DB writes.
   * Not called for zero-turn calls (e.g. the user hung up immediately).
   */
  onCallEnded?: (turns: VoiceTurn[]) => void;
  /**
   * FRONT-254: present only on the hands-free provider's call. Its features
   * switch on only when the server ALSO minted the session hands-free (the
   * mint response says so) — a browser that thinks the flag is on can never
   * add a tool or a screen note to a session the server did not offer them.
   */
  handsFree?: HandsFreeCallHooks;
}

/** FRONT-254: what a hands-free call reports to, and asks of, its host. */
export interface HandsFreeCallHooks {
  /** Apply a screen move the model asked for. Resolves with what to tell
   *  the model: ok, or a sentence saying why nothing moved. */
  onClientAction: (args: unknown) => Promise<{ ok: true } | { ok: false; error: string }>;
  /** The latest screen note, resent on every GPT-Live delegation. */
  getScreenNote: () => string | null;
  /** Someone spoke: the user (a transcribed turn) or EDDY (a reply). */
  onActivity: () => void;
  /** Tool work started/finished — a call waiting on work is not quiet. */
  onWorkChange: (delta: 1 | -1) => void;
}

/** FRONT-254: how long a screen move waits for EDDY's spoken heads-up to
 *  finish playing before moving anyway. Over WebRTC the tool call arrives
 *  while the line is still playing (measured), and the "audio stopped" event
 *  is reported to go missing while a tool call is pending, so it is never
 *  waited on without a cap. */
const NAVIGATE_PLAYBACK_WAIT_MS = 2_500;
const NAVIGATE_PLAYBACK_POLL_MS = 100;
/** A goodbye runs longer than a heads-up, and cutting it off sounds rude. */
const END_CALL_PLAYBACK_WAIT_MS = 6_000;
/** GPT-Live starts the goodbye only once the result reaches it. */
const LIVE_GOODBYE_START_WAIT_MS = 4_000;
/** FRONT-254: a late goodbye is signalled BEFORE the server's sideband
 *  append (ack capped at 8s), so its reading can start later — and it may
 *  open with the result the request was waiting on, so it can run longer. */
const LIVE_LATE_GOODBYE_START_WAIT_MS = 12_000;
const LIVE_LATE_GOODBYE_PLAYBACK_WAIT_MS = 20_000;

/**
 * FRONT-254: what a connection drop means for a live hands-free call.
 * 'disconnected' is often a brief ICE blip that recovers on its own, so an
 * always-on call waits for 'failed' before ending; null leaves the drop to
 * the ordinary error path (every other call, and a call not yet live).
 */
export function handsFreeDropAction(connectionState: string, liveHandsFree: boolean): 'wait' | 'end' | null {
  if (!liveHandsFree) return null;
  if (connectionState === 'disconnected') return 'wait';
  if (connectionState === 'failed') return 'end';
  return null;
}

function endHandsFreeCall(stop: () => void, message = 'The call ended. Your recap is on its way.'): void {
  toast.info(message);
  stop();
}

/** FRONT-254: the standard engine's screen note — a silent system item.
 *  Measured: never triggers a reply, even with server VAD's auto-response on. */
export function realtimeScreenNoteFrame(note: string): Record<string, unknown> {
  return {
    type: 'conversation.item.create',
    item: { type: 'message', role: 'system', content: [{ type: 'input_text', text: note }] },
  };
}

/** FRONT-254: GPT-Live's screen note — OpenAI's documented silent "share UI
 *  context" event, not tied to any delegation. */
export function liveScreenNoteFrame(note: string, eventId: string): Record<string, unknown> {
  return { type: 'session.thinking.append', event_id: eventId, delegation_id: null, content: note };
}

/** FRONT-254: the server minted this session hands-free — the only signal
 *  the browser's hands-free features key off, on both transports. */
export function mintedHandsFree(response: unknown): boolean {
  return !!response && typeof response === 'object' && (response as { handsFree?: unknown }).handsFree === true;
}

interface EphemeralSession {
  // GA shape (Feb 2026): ephemeral key + expiry sit at the top level.
  // The Realtime model + audio + tool config is nested under `session`.
  value?: string;
  expires_at?: number;
  session?: { model?: string };
  // Older preview shape (some docs still reference it).
  client_secret?: { value?: string; expires_at?: number };
  /** FRONT-1290: the region host this secret was minted against. */
  callsUrl?: string;
  [k: string]: unknown;
}

const OPENAI_REALTIME_CALLS = 'https://api.openai.com/v1/realtime/calls';

/**
 * FRONT-1290: the host to run the WebRTC exchange against.
 *
 * The server mints the ephemeral secret against a configured OpenAI region
 * (the EU project by default, for data residency) and names that host on the
 * mint response as `callsUrl`. A secret minted in one region is not valid in
 * the other, so the browser must follow the server rather than keep its own
 * constant — the constant stays only as the fallback for a mint response that
 * predates the field.
 *
 * Only an absolute https OpenAI URL is accepted: this value decides where the
 * call audio goes, and the ephemeral secret travels with it.
 */
export function resolveRealtimeCallsUrl(callsUrl: unknown): string {
  if (typeof callsUrl !== 'string' || callsUrl.length === 0) return OPENAI_REALTIME_CALLS;
  try {
    const url = new URL(callsUrl);
    const openAiHost = url.hostname === 'api.openai.com' || url.hostname.endsWith('.api.openai.com');
    if (url.protocol !== 'https:' || !openAiHost) return OPENAI_REALTIME_CALLS;
    return url.toString();
  } catch {
    return OPENAI_REALTIME_CALLS;
  }
}

/**
 * FRONT-531: which chat session a voice call persists to. A surface-supplied
 * override (the zen modal's own `web:<uuid>` thread) wins; otherwise the global
 * canonical session is used. Extracted so the binding rule — the exact bit that
 * regressed into orphaned-thread calls during the FRONT-518 rebuild — is
 * unit-pinned independent of the WebRTC plumbing.
 */
export function resolveVoiceSessionId(
  override: string | undefined,
  fallback: string,
): string {
  return override && override.length > 0 ? override : fallback;
}

function pickEphemeralValue(session: EphemeralSession): string | null {
  // Prefer the GA top-level `value`; fall back to the older nested shape so
  // a future OpenAI rollback doesn't break us.
  const value = session?.value ?? session?.client_secret?.value;
  return typeof value === 'string' && value.length > 0 ? value : null;
}

/** FRONT-2001: the `POST /api/voice/realtime/session` path decision hands
 *  back `{transport:"live", model}` instead of minting an ephemeral secret
 *  when the caller's saved pick is a Live model and the flag is on for
 *  their org — every other shape (no `transport` field at all) stays the
 *  realtime mint this hook already knows how to read. */
interface LivePathResponse {
  transport?: string;
  model?: string;
}

/**
 * FRONT-2001: the one predicate that decides whether a parsed
 * `/api/voice/realtime/session` response is a Live hand-off rather than a
 * realtime ephemeral-secret mint. Pulled out of `start()` and exported so it
 * can be pinned directly — a realtime response (the pre-existing
 * `{value, session:{model}, ...}` shape, or the older `client_secret`
 * shape) must never be misread as Live just because it happens to carry a
 * truthy or oddly-typed `transport` field.
 */
export function isLiveTransportResponse(response: unknown): boolean {
  return (
    !!response &&
    typeof response === 'object' &&
    (response as { transport?: unknown }).transport === 'live'
  );
}

/**
 * Review S5: whether a Live delegation result should still be applied
 * through the SPECIFIC tracker it was dispatched against. `runLiveDelegation`
 * closes over that tracker (and its data channel) at dispatch time and never
 * re-reads `refs.current` after the fetch awaits — so this only ever sees the
 * one tracker the result actually belongs to, never a later call's. Pulled
 * out as its own function (rather than inlined as `!tracker.isStopped`) so
 * the drop decision is pinned by a plain-node test independent of React,
 * jsdom, or WebRTC: given tracker A stopped and tracker B still live, a
 * result bound to A is dropped and one bound to B is not — proving a result
 * for one call's tracker can never be waved through as if it were another's.
 */
export function shouldApplyLiveDelegationResult(tracker: LiveCallTracker): boolean {
  return !tracker.isStopped;
}

/** How long after a handed-off delegation this tab re-reads the chat's
 *  background run — the server opens that run right after it answers (see
 *  `live-history-refresh.ts`). */
const LIVE_BACKGROUND_RECHECK_DELAY_MS = 2_000;

/** R2-5: the two Live transcript-delta event names fire on nearly every
 *  audio chunk and would drown the `[voice-live]` debug log — filtered out
 *  of it. (The prior filter checked `response.audio_transcript.delta`, a
 *  REALTIME name; Live's own delta types, per `live-event-router.ts`, are
 *  these two.) Pulled out as a pure predicate so the filter itself is
 *  pinned independent of the data-channel/WebRTC plumbing around it. */
export function shouldLogLiveEvent(eventType: string | undefined): boolean {
  return eventType !== 'session.input_transcript.delta' && eventType !== 'session.output_transcript.delta';
}

/** How often the hook ticks a live call's tracker (final-plan.md §3-14: "Re-
 *  evaluated on a 250ms tick"). */
const LIVE_TICK_MS = 250;
/** R2-3: mirrors `MAX_ASSET_CONTEXT_CHARS` in `src/voice/realtime-prompt.ts`
 *  exactly — the prompt's own cap, so `withAssetContext` below never ships
 *  more of the document than the server would keep anyway. Truncating here
 *  also matters on its own terms: the full document body could otherwise
 *  push a delegate request over the route's 64KB body cap and turn every
 *  delegation into a 400. */
const MAX_ASSET_CONTEXT_CHARS = 8_000;

/** R2-3 / N4: splice the session's `assetContext` onto a delegate request
 *  body right before it goes out — the tracker itself never sets this (it
 *  has no notion of the launching chat's anchored document), so it can't be
 *  part of `LiveCallTracker.buildRequestBody`. Cut to the prompt's own cap
 *  (see `MAX_ASSET_CONTEXT_CHARS`). Omitted entirely — never sent as `""` —
 *  when the session has none, matching `pending`'s own "present means
 *  something, absent means nothing to say" convention on this wire shape. */
/** FRONT-254: splice the latest screen note onto a delegate request on a
 *  hands-free call, same "omit when there is none" convention as
 *  `withAssetContext`. Capped at the route's own 500-char limit. */
export function withScreenNote(body: LiveDelegateRequestBody, note: string | null): LiveDelegateRequestBody {
  if (!note) return body;
  return { ...body, screen: note.slice(0, 500) };
}

export function withAssetContext(
  body: LiveDelegateRequestBody,
  assetContext: string | undefined,
): LiveDelegateRequestBody {
  if (!assetContext) return body;
  return { ...body, assetContext: assetContext.slice(0, MAX_ASSET_CONTEXT_CHARS) };
}

/** FRONT-2001 round 3 (§7): the fetch url + init for `POST /api/voice/live/
 *  ended` — pulled out pure so the shape (method, headers, `keepalive`, body)
 *  can be pinned without rendering the hook. `keepalive: true` matters here
 *  specifically: `stop()`/`cleanup()` call this right as the tab may be
 *  navigating away or the call's own teardown is already underway, and a
 *  non-keepalive fetch can be cancelled by the browser mid-flight in that
 *  window. Same `Content-Type` + timezone-header pattern every other voice
 *  fetch in this file sends (see `/api/voice/live/delegate` above). */
export function buildLiveEndedRequest(liveSessionId: string): { url: string; init: RequestInit } {
  return {
    url: '/api/voice/live/ended',
    init: {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...timezoneHeader() },
      body: JSON.stringify({ liveSessionId }),
      keepalive: true,
    },
  };
}
/**
 * GPT-Live: the goodbye is spoken only after the result reaches the model,
 * so first wait (briefly) for it to start, then for it to stop. Capped both
 * ways, so a goodbye that never plays still ends the call.
 */
async function waitForLiveGoodbye(
  isSpeaking: (now: number) => boolean,
  startWaitMs: number = LIVE_GOODBYE_START_WAIT_MS,
  playbackWaitMs: number = END_CALL_PLAYBACK_WAIT_MS,
): Promise<void> {
  const startBy = Date.now() + startWaitMs;
  while (!isSpeaking(Date.now()) && Date.now() < startBy) {
    await new Promise((resolve) => setTimeout(resolve, NAVIGATE_PLAYBACK_POLL_MS));
  }
  await waitForPlaybackToFinish(() => isSpeaking(Date.now()), playbackWaitMs);
}

/** Wait, capped, until EDDY's current spoken line has finished playing. */
async function waitForPlaybackToFinish(isSpeaking: () => boolean, waitMs: number = NAVIGATE_PLAYBACK_WAIT_MS): Promise<void> {
  const deadline = Date.now() + waitMs;
  while (isSpeaking() && Date.now() < deadline) {
    await new Promise((resolve) => setTimeout(resolve, NAVIGATE_PLAYBACK_POLL_MS));
  }
}

/**
 * FRONT-254: run one `ui_navigate` on a hands-free call. Waits (capped) for
 * EDDY's spoken heads-up to finish so the screen doesn't move mid-sentence,
 * then hands the arguments to the host. Never throws: anything unexpected
 * becomes a refusal the model can say.
 */
export async function runClientNavigate(
  hooks: Pick<HandsFreeCallHooks, 'onClientAction'>,
  argumentsJson: string,
  isSpeaking: () => boolean,
  waitMs: number = NAVIGATE_PLAYBACK_WAIT_MS,
): Promise<{ ok: true } | { ok: false; error: string }> {
  let args: unknown;
  try {
    args = argumentsJson ? JSON.parse(argumentsJson) : {};
  } catch {
    return { ok: false, error: "That request to open something was garbled, so nothing moved." };
  }
  await waitForPlaybackToFinish(isSpeaking, waitMs);
  try {
    return await hooks.onClientAction(args);
  } catch {
    return { ok: false, error: "Opening that didn't work, so the screen stayed where it was." };
  }
}

/** OpenAI's Live connect route relays the browser's offer straight to its
 *  REST create endpoint rather than a streaming dial-out, so it needs a
 *  fully ICE-gathered offer up front — capped so a network that never
 *  completes gathering (e.g. no STUN reachable) doesn't hang the call start
 *  forever. */
const ICE_GATHERING_TIMEOUT_MS = 3_000;
const LIVE_DELEGATE_TIMEOUT_MS = 40_000;
/** The fixed line spoken when a delegate call never comes back in time or
 *  answers with anything other than 200 (final-plan.md §2-D contract). */
const LIVE_DELEGATE_LOST_MESSAGE =
  "I lost track of that request — please check the chat before asking again.";

/** Resolves once `pc`'s ICE gathering completes, or after `timeoutMs`,
 *  whichever comes first — never rejects, since a live call is still worth
 *  attempting with a partial candidate set rather than failing outright. */
function waitForIceGatheringComplete(pc: RTCPeerConnection, timeoutMs: number): Promise<void> {
  if (pc.iceGatheringState === 'complete') return Promise.resolve();
  return new Promise((resolve) => {
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      pc.removeEventListener('icegatheringstatechange', onChange);
      clearTimeout(timer);
      resolve();
    };
    const onChange = () => {
      if (pc.iceGatheringState === 'complete') finish();
    };
    pc.addEventListener('icegatheringstatechange', onChange);
    const timer = setTimeout(finish, timeoutMs);
  });
}

// All in-call mutable state lives in refs so the hook's React state stays
// minimal (state machine + status only). RTCPeerConnection and friends
// don't belong in React state — they aren't comparable, mutating them
// shouldn't trigger renders, and they need cleanup that's awkward inside
// dependency arrays.
interface CallRefs {
  pc: RTCPeerConnection | null;
  dc: RTCDataChannel | null;
  mic: MediaStream | null;
  audio: HTMLAudioElement | null;
  assistantBuffer: string;
  // FRONT-1469: owns the response.create/response.done bookkeeping so a
  // fast tool result or a parallel-tool turn never sends a second
  // `response.create` while one is still active. Per-call state — a fresh
  // instance every time `createEmptyRefs()` runs (initial mount and every
  // `cleanup()`) so a new call always starts with a clean gate.
  gate: VoiceTurnGate;
  /** FRONT-2001: set once a GPT-Live-1 connect response is applied inside
   *  `startLive` — null for every realtime call. Its presence is what the
   *  shared `stop()`/`toggleMute()` branches key off to tell the transports
   *  apart, since a realtime call never has one. */
  liveTracker: LiveCallTracker | null;
  /** The 250ms tick interval driving `liveTracker.tick(...)` — cleared on
   *  cleanup so a torn-down call's tracker doesn't keep producing status
   *  effects into a hook that has moved on. */
  liveTickHandle: ReturnType<typeof setInterval> | null;
  /** FRONT-2509: the Realtime call's cost anchor from the mint; null for a
   *  Live call and for a server that predates it. Cleared once the hang-up
   *  is reported, so a second cleanup() never reports it twice. */
  usageCallId: string | null;
  /** FRONT-2509: the peer actually connected to OpenAI, so the call cost
   *  something; a call that never got this far is closed with no row. */
  usageConnected: boolean;
  /** FRONT-254: the server minted this call hands-free AND the host passed
   *  its hooks. False for every other call. */
  handsFree: boolean;
  /** FRONT-254: EDDY's audio is playing (WebRTC playback events). */
  speaking: boolean;
}

function createEmptyRefs(): CallRefs {
  return {
    pc: null,
    dc: null,
    mic: null,
    audio: null,
    assistantBuffer: '',
    gate: new VoiceTurnGate(),
    liveTracker: null,
    liveTickHandle: null,
    usageCallId: null,
    usageConnected: false,
    handsFree: false,
    speaking: false,
  };
}

/**
 * FRONT-2509: tell the server a Realtime call is over, for the cost ledger.
 * Fire-and-forget: a lost notice only means a call that reported nothing is
 * not estimated, it never touches the call itself.
 */
function sendRealtimeHangup(callId: string, connected: boolean): void {
  const { url: realtimeEndedUrl, init: realtimeEndedInit } = buildRealtimeEndedRequest(callId, connected);
  fetch(realtimeEndedUrl, realtimeEndedInit).catch((err: Error) => {
    console.debug('[voice-call] /realtime/usage/end notification threw', err.message);
  });
}

export function useVoiceCall(options: UseVoiceCallOptions) {
  const { branchId, assetContext, scopeEntityId, onAssistantDelta, onAssistantFinal, onUserTranscript, onCallEnded, handsFree } =
    options;
  const { sessionId: globalSessionId } = useSession();
  // FRONT-531: prefer the surface's own thread id so the call lands where the
  // user is looking; fall back to the global session for legacy surfaces.
  const sessionId = resolveVoiceSessionId(options.sessionId, globalSessionId);
  const queryClient = useQueryClient();

  const [state, setState] = useState<VoiceCallState>('idle');
  const [status, setStatus] = useState<VoiceStatus>('listening');
  const [error, setError] = useState<string | null>(null);
  // Mute is a property of the local mic track, not of the call: disabling the
  // track keeps the peer connection and the timer alive while the far side
  // simply hears silence. Mirrored in React state so the UI can show it.
  const [muted, setMuted] = useState(false);
  // FRONT-3268: a hands-free call waiting out a brief connection drop (see
  // `handsFreeDropAction`), so a surface can say so instead of looking dead.
  const [reconnecting, setReconnecting] = useState(false);

  const refs = useRef<CallRefs>(createEmptyRefs());
  // Stash callbacks in refs so the data-channel handler closure always
  // sees the latest ones without re-binding the channel on every render.
  const cbRefs = useRef({ onAssistantDelta, onAssistantFinal, onUserTranscript, onCallEnded, handsFree });
  useEffect(() => {
    cbRefs.current = { onAssistantDelta, onAssistantFinal, onUserTranscript, onCallEnded, handsFree };
  }, [onAssistantDelta, onAssistantFinal, onUserTranscript, onCallEnded, handsFree]);

  // Track whether at least one turn (user or assistant) was exchanged this
  // call. Reset to false at the start of each call so repeated calls each
  // get a fresh count. Stored in a ref — it's mutable state the closures
  // inside handleEvent and stop() need to share, not React render state.
  const hadTurnRef = useRef(false);
  /**
   * Monotonic call id. Bumped on every start and every stop, so an in-flight
   * `start()` can tell whether the call it is building is still the one the
   * user wants — see the note in `start`.
   */
  const callGenerationRef = useRef(0);
  // Full transcript of the current call, in order. Handed to onCallEnded so
  // the end-of-call endpoint can read the conversation straight from memory
  // instead of re-reading chat_messages (which may not have committed the
  // final turns yet — that race is what made the save card never appear).
  const turnsRef = useRef<VoiceTurn[]>([]);

  const sessionInfoRef = useRef({ sessionId, branchId, assetContext, scopeEntityId });
  useEffect(() => {
    sessionInfoRef.current = { sessionId, branchId, assetContext, scopeEntityId };
  }, [sessionId, branchId, assetContext, scopeEntityId]);

  // FRONT-2001: three small pieces of Live-only plumbing that live outside
  // `refs` (a `CallRefs`) on purpose, each for its own reason:
  //   - `pendingLiveCloseRef` must survive `stop()` resetting `refs.current`
  //     to a fresh object for the NEXT call, since it's how the still-live
  //     data channel's close ack (or the 1.5s timeout) finds the teardown to
  //     run for the call that's actually ending.
  //   - `applyLiveEffectsRef`/`stopRef` break what would otherwise be a
  //     circular `useCallback` dependency (applying an effect can trigger a
  //     stop; stop applies effects) without giving up a stable identity for
  //     `start`/`stop` — see the note above `stop`'s own definition.
  const pendingLiveCloseRef = useRef<(() => void) | null>(null);
  const applyLiveEffectsRef = useRef<
    (tracker: LiveCallTracker, dc: RTCDataChannel | null, effects: LiveTrackerEffect[]) => void
  >(() => {});
  const stopRef = useRef<() => void>(() => {});

  // FRONT-2001 round 3 (§7): tell the server a Live call is over so the
  // late-result delivery path (src/voice/live.ts's `/ended` route) knows
  // `ended_at` is set and can stop trying the voice sideband for anything
  // still handed off. Fire-and-forget — never surfaced to the user, since a
  // failed notification only costs a slightly later fallback to chat-only
  // delivery (the sweeper's own stale-heartbeat sweep is the ultimate
  // backstop). A no-op for a realtime call (tracker is null there) and for a
  // second stop()/cleanup() on the same tracker (`claimEndedSend` below).
  const notifyLiveEnded = useCallback((tracker: LiveCallTracker | null) => {
    if (!tracker || !tracker.claimEndedSend()) return;
    // Named distinctly (not `url`/`init`) so this call site reads as its own
    // request rather than a generic fetch — and so it can be named in
    // `timezone-header-guard.test.ts`'s EXEMPT_MARKERS: the device-timezone
    // header is built inside `buildLiveEndedRequest` itself, not spelled out
    // at this call site, and is pinned by that function's own shape test.
    const { url: endedUrl, init: endedInit } = buildLiveEndedRequest(tracker.liveSessionId);
    fetch(endedUrl, endedInit)
      .then((res) => {
        if (!res.ok) console.debug('[voice-live] /ended notification failed', res.status);
      })
      .catch((err) => {
        console.debug('[voice-live] /ended notification threw', err);
      });
  }, []);

  const cleanup = useCallback(() => {
    const r = refs.current;
    // FRONT-2509: tell the server a Realtime call is over, so a call that
    // never reported a response is still billed from its length. Every path
    // that ends a Realtime call runs through here.
    if (r.usageCallId) {
      sendRealtimeHangup(r.usageCallId, r.usageConnected);
      r.usageCallId = null;
    }
    // Review S5: the 'failed'/'disconnected' error path in startLive's
    // onconnectionstatechange calls cleanup() WITHOUT ever going through
    // stop()'s own `tracker.stop()` — leaving the tracker not-stopped, so a
    // delegate fetch already in flight for this call could still land its
    // result (and a stray commentary/close frame) through whatever call
    // `refs.current` has moved on to by the time it resolves. It also never
    // ran stop()'s own /ended notification, so this path sends its own —
    // `claimEndedSend()` on the tracker is what keeps a call that reaches
    // BOTH stop() and this branch from notifying twice.
    notifyLiveEnded(r.liveTracker);
    r.liveTracker?.stop();
    try { r.dc?.close(); } catch { /* ignore */ }
    try { r.pc?.getSenders().forEach((s) => s.track?.stop()); } catch { /* ignore */ }
    try { r.pc?.close(); } catch { /* ignore */ }
    r.mic?.getTracks().forEach((t) => t.stop());
    if (r.audio) {
      try { r.audio.pause(); } catch { /* ignore */ }
      r.audio.srcObject = null;
      r.audio.remove();
    }
    // FRONT-2001: a no-op for every realtime call (liveTickHandle is always
    // null there) — only matters for a live call torn down via the
    // 'failed'/'disconnected' branch in startLive's onconnectionstatechange,
    // where nothing else has stopped this interval yet.
    if (r.liveTickHandle) clearInterval(r.liveTickHandle);
    refs.current = createEmptyRefs();
  }, [notifyLiveEnded]);

  useEffect(() => () => cleanup(), [cleanup]);

  // FRONT-2509: a closed tab never runs React's unmount cleanup, so report
  // the hang-up on `pagehide` too — the keepalive requests outlive the page.
  // Idempotent with a later cleanup(): the Realtime id is cleared once sent,
  // and the Live notice is claimed once per tracker.
  useEffect(() => {
    const onPageHide = () => {
      const r = refs.current;
      notifyLiveEnded(r.liveTracker);
      if (r.usageCallId) {
        sendRealtimeHangup(r.usageCallId, r.usageConnected);
        r.usageCallId = null;
      }
    };
    window.addEventListener('pagehide', onPageHide);
    return () => window.removeEventListener('pagehide', onPageHide);
  }, [notifyLiveEnded]);

  // Persist a single voice turn into chat_messages. Best-effort — a failed
  // POST is logged and surfaced via toast but doesn't tear the call down.
  // The /history refetch on call-end is what makes the turn visible after
  // a page reload.
  const persistTurn = useCallback(
    async (role: 'user' | 'assistant', content: string) => {
      const text = content.trim();
      if (!text) return;
      const { sessionId: sid, branchId: bid } = sessionInfoRef.current;
      try {
        const res = await fetch('/api/voice/realtime/persist', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            sessionId: sid,
            branchId: bid,
            role,
            content: text,
          }),
        });
        if (!res.ok) {
          const detail = await res.text().catch(() => '');
          console.warn('[voice-call] persist failed:', res.status, detail.slice(0, 200));
        } else if (refs.current.handsFree) {
          // FRONT-254: a hands-free call keeps its words on the strip, not
          // in the chat view, so the open chat reads along by refetching
          // its history after each saved turn.
          void queryClient.invalidateQueries({ queryKey: chatKeys.history(sid, bid) });
        }
      } catch (err) {
        console.warn('[voice-call] persist threw:', err);
      }
    },
    [queryClient],
  );

  // FRONT-2001: the common tail of a closed user/assistant turn — turnsRef,
  // hadTurnRef, the surface callback and persistTurn — factored out of
  // realtime's `handleEvent` switch so both transports land on the exact
  // same path (final-plan.md §2-E: "persistTurn/turnsRef/surface callbacks"
  // shared). Realtime keeps its own buffer-fallback logic at its call site
  // (a `…transcript.done` event sometimes ships without the full text) —
  // Live's tracker always hands over its own complete accumulated text, so
  // it never needs that fallback.
  const finishUserTurn = useCallback(
    (text: string) => {
      hadTurnRef.current = true;
      if (text.trim()) turnsRef.current.push({ role: 'user', content: text.trim() });
      // FRONT-254: only a transcribed turn WITH words counts as the user
      // speaking — a room-noise turn that transcribes to nothing does not
      // keep a forgotten call alive.
      if (text.trim() && refs.current.handsFree) cbRefs.current.handsFree?.onActivity();
      cbRefs.current.onUserTranscript?.(text);
      void persistTurn('user', text);
    },
    [persistTurn],
  );

  const finishAssistantTurn = useCallback(
    (text: string, alreadySaved = false) => {
      hadTurnRef.current = true;
      if (text.trim()) turnsRef.current.push({ role: 'assistant', content: text.trim() });
      cbRefs.current.onAssistantFinal?.(text);
      // FRONT-254: the spoken reading of a late result the server already
      // wrote into the chat — saving it would show the answer twice.
      if (!alreadySaved) void persistTurn('assistant', text);
    },
    [persistTurn],
  );

  const applyAssistantDelta = useCallback((delta: string) => {
    if (refs.current.handsFree) cbRefs.current.handsFree?.onActivity();
    cbRefs.current.onAssistantDelta?.(delta);
  }, []);

  // Review S5: takes the data channel EXPLICITLY — the caller passes the
  // one bound to whichever tracker raised the effect, never re-read from
  // `refs.current.dc`, which may already belong to a different call by the
  // time an async effect (a delegation result) gets here.
  const sendLiveFrame = useCallback((dc: RTCDataChannel | null, frame: Record<string, unknown>) => {
    if (!dc || dc.readyState !== 'open') return;
    try {
      dc.send(JSON.stringify(frame));
    } catch (err) {
      console.warn('[voice-call] failed to send live frame:', err);
    }
  }, []);

  // Only ever reached for a call the USER hasn't already asked to end
  // (stop()'s own graceful-close path resolves `session.closed` through
  // `pendingLiveCloseRef` directly, without going through the tracker at
  // all — see the data-channel handler in `startLive`). So a reason other
  // than close_requested here means the server ended the call out from
  // under us; final-plan.md §2-F/§3 item 3 asks for a plain-words toast
  // (never the raw protocol reason) plus running the same stop() path a
  // hangup takes, so turns-so-far still get saved.
  const handleLiveRemoteEnd = useCallback((reason: string | undefined) => {
    // FRONT-254: a hands-free call that hits the vendor's time limit is an
    // ordinary end — same recap path, no error.
    if (reason === 'expired' && refs.current.handsFree) {
      toast.info('The call reached its time limit and ended. Your recap is on its way.');
    } else if (reason !== 'close_requested') {
      console.warn('[voice-call] live session closed unexpectedly:', reason);
      toast.error("The call disconnected unexpectedly — your turns so far are saved.");
    }
    stopRef.current();
  }, []);

  // POST /api/voice/live/delegate for one settled delegation, aborting at
  // 40s (final-plan.md §2-D/§4). On anything other than a 200 — a non-OK
  // status, a thrown fetch, or the abort itself — the delegation still
  // completes, with the fixed line the contract specifies, so the model
  // always gets an answer to speak rather than hanging silently. Takes the
  // SPECIFIC tracker instance AND data channel the effect was raised on
  // (review S5 — never re-read via `refs.current` after the await) so a
  // result for a call that has since ended is applied through that call's
  // own (by-then stopped) tracker/channel instead of whatever a brand-new
  // call's tracker/channel happens to be.
  const runLiveDelegation = useCallback(
    async (
      tracker: LiveCallTracker,
      dc: RTCDataChannel | null,
      delegationId: string,
      body: LiveDelegateRequestBody,
    ) => {
      let content = LIVE_DELEGATE_LOST_MESSAGE;
      // Diagnosability: the server's own outcome enum (never spoken content)
      // — logged below so a call that "heard the words but never reached
      // the delegate route" is distinguishable from one that reached it and
      // got an honest failure outcome back.
      let outcome = 'lost';
      // FRONT-2001 round 3: whether this delegation was handed off to keep
      // running in the background past this response, and whether it was
      // answered early via the `voice_lines` pseudo-tool (§4.6/§6) — both
      // `undefined` for a response shape that doesn't carry them (the lost/
      // threw/http_* fallback paths below never touch these).
      let handedOff: boolean | undefined;
      let early: boolean | undefined;
      // FRONT-254: a screen move riding on this result (hands-free only).
      let clientAction: unknown;
      // FRONT-254: hang up once this result is spoken (hands-free only).
      let endCall = false;
      // FRONT-254: read once, at dispatch — the hooks belong to THIS call.
      const hf = refs.current.handsFree ? cbRefs.current.handsFree : undefined;
      hf?.onWorkChange(1);
      try {
        // R2-3: the delegate route folds `assetContext` into the resolver's
        // own prompt exactly like the connect call did — the tracker's body
        // never carries it (see `withAssetContext`'s own doc), so it's
        // spliced on here from the same session info the connect call read.
        const requestBody = withScreenNote(
          withAssetContext(body, sessionInfoRef.current.assetContext),
          hf?.getScreenNote() ?? null,
        );
        // FRONT-254: same opt-in as the connect call; the server re-checks the flag.
        const delegateBody = hf ? { ...requestBody, handsFree: true } : requestBody;
        const res = await fetch('/api/voice/live/delegate', {
          method: 'POST',
          // FRONT-1507: the reader's own device timezone, same as every
          // other backend call this hook makes — the resolver localizes
          // any timestamp a dispatched tool result carries.
          headers: { 'Content-Type': 'application/json', ...timezoneHeader() },
          body: JSON.stringify(delegateBody),
          signal: AbortSignal.timeout(LIVE_DELEGATE_TIMEOUT_MS),
        });
        if (res.ok) {
          const data = (await res.json()) as {
            content?: string;
            outcome?: string;
            handedOff?: boolean;
            early?: boolean;
            clientAction?: unknown;
            endCall?: boolean;
          };
          if (typeof data.content === 'string' && data.content) content = data.content;
          if (hf && data.clientAction) clientAction = data.clientAction;
          if (hf && data.endCall === true) endCall = true;
          if (typeof data.outcome === 'string') outcome = data.outcome;
          if (typeof data.handedOff === 'boolean') handedOff = data.handedOff;
          if (typeof data.early === 'boolean') early = data.early;
        } else {
          outcome = `http_${res.status}`;
          console.warn('[voice-call] live delegate failed:', res.status, delegationId);
        }
      } catch (err) {
        outcome = 'threw';
        console.warn('[voice-call] live delegate threw:', err, delegationId);
      }
      // A hand-off keeps working on the server, possibly on a different pod
      // than this tab's live-update stream. Re-read this chat's background
      // run once it exists, so the history refresh can follow it to the
      // settle — also after a hang-up, since the result lands in the chat.
      if (handedOff) {
        const sid = sessionInfoRef.current.sessionId;
        if (sid) {
          setTimeout(() => {
            queryClient.invalidateQueries({ queryKey: backgroundChatKeys.bySession(sid) });
          }, LIVE_BACKGROUND_RECHECK_DELAY_MS);
        }
      }
      hf?.onWorkChange(-1);
      if (!shouldApplyLiveDelegationResult(tracker)) {
        console.debug('[voice-live] delegation dropped (stopped)', delegationId);
        return;
      }
      console.debug('[voice-live] delegation completed', outcome, delegationId, { handedOff, early });
      applyLiveEffectsRef.current(tracker, dc, tracker.delegationDone({ content }, Date.now()));
      // FRONT-254: the model's filler line was already spoken when it
      // delegated (measured ~0.2 s in), so the screen moves as the result
      // is read out. A failed move cannot be un-said; it is surfaced instead.
      if (hf && clientAction !== undefined) {
        void hf.onClientAction(clientAction).then((r) => {
          if (!r.ok) toast.error(r.error);
        });
      }
      // FRONT-254: the result is the goodbye. Let it play, then hang up.
      if (hf && endCall) {
        void waitForLiveGoodbye((now) => tracker.isSpeaking(now)).then(() => {
          if (refs.current.liveTracker === tracker && !tracker.isStopped) {
            endHandsFreeCall(stopRef.current, 'EDDY ended the call. Your recap is on its way.');
          }
        });
      }
    },
    [queryClient],
  );

  // FRONT-2001: apply a batch of LiveCallTracker effects onto the same code
  // paths realtime's handleEvent switch already uses (final-plan.md §2-E).
  // Review S5: takes the SOURCE tracker/dc explicitly and threads them into
  // every effect that itself produces a later async callback ('run-
  // delegation') or an immediate send ('send-frame'), rather than letting
  // those read `refs.current` fresh — which may have moved on to a
  // different call by the time an async one resolves. Depends only on
  // stable callbacks (finishUserTurn/finishAssistantTurn/applyAssistantDelta/
  // sendLiveFrame/handleLiveRemoteEnd/runLiveDelegation all have fixed
  // dependency arrays), so this itself never churns identity across renders.
  const applyLiveEffects = useCallback(
    (tracker: LiveCallTracker, dc: RTCDataChannel | null, effects: LiveTrackerEffect[]) => {
      for (const effect of effects) {
        switch (effect.kind) {
          case 'status':
            setStatus(effect.status);
            break;
          case 'user-transcript':
            finishUserTurn(effect.text);
            break;
          case 'assistant-delta':
            applyAssistantDelta(effect.delta);
            break;
          case 'assistant-final':
            finishAssistantTurn(effect.text, effect.alreadySaved === true);
            break;
          case 'run-delegation':
            console.debug('[voice-live] delegation dispatched', effect.delegationId);
            void runLiveDelegation(tracker, dc, effect.delegationId, effect.body);
            break;
          case 'send-frame':
            sendLiveFrame(dc, effect.frame);
            break;
          case 'remote-end':
            handleLiveRemoteEnd(effect.reason);
            break;
          case 'error':
            // Scope removal (review): no toast for a data-channel error —
            // the call stays up either way, and a routine
            // `context_injection_incomplete` at close would otherwise toast
            // on every ordinary hangup. Remote end (session.closed with a
            // reason other than close_requested, handled above) keeps its
            // own toast.
            console.warn('[voice-call] live data-channel error:', effect.message);
            break;
        }
      }
    },
    [finishUserTurn, finishAssistantTurn, applyAssistantDelta, sendLiveFrame, handleLiveRemoteEnd, runLiveDelegation],
  );
  applyLiveEffectsRef.current = applyLiveEffects;

  // FRONT-254: the server is about to speak a late result into a GPT-Live
  // call (see `live-late-result-signal.ts`). Its reading is marked as
  // already saved, and a hang-up asked for after the call's own answer went
  // out is carried out once that reading — the goodbye — has played.
  useEffect(
    () =>
      subscribeLiveLateResult((signal) => {
        const r = refs.current;
        const tracker = r.liveTracker;
        const action = lateResultSignalAction(signal, {
          liveSessionId: tracker?.liveSessionId ?? null,
          stopped: tracker?.isStopped ?? true,
          handsFree: r.handsFree,
        });
        if (!tracker || !action.markReading) return;
        tracker.lateResultIncoming(Date.now());
        if (!action.hangUp) return;
        void waitForLiveGoodbye(
          (now) => tracker.isSpeaking(now),
          LIVE_LATE_GOODBYE_START_WAIT_MS,
          LIVE_LATE_GOODBYE_PLAYBACK_WAIT_MS,
        ).then(() => {
          if (refs.current.liveTracker === tracker && !tracker.isStopped) {
            endHandsFreeCall(stopRef.current, 'EDDY ended the call. Your recap is on its way.');
          }
        });
      }),
    [],
  );

  // Forward a tool call from the realtime model: POST to /tool, then send
  // the result back over the data channel. Failures send an error string
  // back so the model can react rather than hang.
  const handleToolCall = useCallback(
    async (callId: string, name: string, argumentsJson: string, responseId?: string) => {
      const dc = refs.current.dc;
      if (!dc || dc.readyState !== 'open') return;
      // FRONT-1469: register the in-flight tool call with the gate. This
      // has to sit in exactly this spot for two reasons: AFTER the
      // `dc.readyState` guard above — registering before it would leak a
      // call id into the gate's `outstanding` map with no result ever
      // coming, since the function returns early and `toolResultReady`
      // would never be called to remove it; and BEFORE the first `await`
      // below, so that parallel tool calls (several in one model turn) all
      // register with the gate before any of them can resolve.
      refs.current.gate.toolCallDispatched(callId, responseId, Date.now());
      // FRONT-254: `ui_navigate` is the browser's own tool on a hands-free
      // call — never sent to the server (which refuses it anyway).
      const hf = refs.current.handsFree ? cbRefs.current.handsFree : undefined;
      if (hf && name === 'ui_navigate') {
        const gate = refs.current.gate;
        // The call can end while the heads-up is still playing: a move for a
        // call that is gone must not happen.
        const outcome = await runClientNavigate(
          { onClientAction: (args) => (refs.current.dc === dc ? hf.onClientAction(args) : Promise.resolve({ ok: false as const, error: 'The call ended.' })) },
          argumentsJson,
          () => refs.current.speaking,
        );
        try {
          // A move that happened needs no reply — asking for one makes EDDY
          // narrate it a second time (measured). A refusal does, so he says
          // what went wrong.
          const frames = gate.toolResultReady(
            callId,
            JSON.stringify(outcome.ok ? { ok: true } : { ok: false, error: outcome.error }),
            Date.now(),
            { silent: outcome.ok },
          );
          for (const frame of frames) dc.send(JSON.stringify(frame));
        } catch (err) {
          console.warn('[voice-call] failed to forward navigate result:', err);
        }
        return;
      }
      // FRONT-254: `end_call` is the browser's too. Let the goodbye finish
      // playing, then hang up exactly as the End button does.
      if (hf && name === 'end_call') {
        await waitForPlaybackToFinish(() => refs.current.speaking, END_CALL_PLAYBACK_WAIT_MS);
        if (refs.current.dc === dc) endHandsFreeCall(stopRef.current, 'EDDY ended the call. Your recap is on its way.');
        return;
      }
      hf?.onWorkChange(1);
      let resultText: string;
      try {
        const res = await fetch('/api/voice/realtime/tool', {
          method: 'POST',
          // FRONT-1507: the reader's own device timezone, so a tool call
          // made mid-call converts timestamps into their local clock.
          headers: { 'Content-Type': 'application/json', ...timezoneHeader() },
          // FRONT-384: forward the voice session id so graph writes made
          // during the call are traceable to this conversation.
          body: JSON.stringify({
            call_id: callId,
            name,
            arguments: argumentsJson,
            sessionId: sessionInfoRef.current.sessionId,
            // FRONT-254: the server re-checks the flag; this says the call was minted hands-free.
            ...(hf ? { handsFree: true } : {}),
          }),
        });
        const data = (await res.json()) as { result?: unknown; error?: string };
        if (!res.ok || data.error) {
          resultText = JSON.stringify({ error: data.error ?? `HTTP ${res.status}` });
        } else {
          resultText = typeof data.result === 'string'
            ? data.result
            : JSON.stringify(data.result ?? {});
        }
      } catch (err) {
        resultText = JSON.stringify({ error: (err as Error).message });
      }
      hf?.onWorkChange(-1);
      // FRONT-254: a chat archived or deleted by voice must leave the lists.
      if (hf && name.startsWith('chat_sessions_')) {
        queryClient.invalidateQueries({ queryKey: chatKeys.threadsRoot() });
      }

      try {
        // FRONT-1469: the gate decides whether a `response.create` belongs
        // in this batch — see the module doc for the coalescing and
        // staleness rules that make this safe against parallel tool calls
        // and a fast tool racing its own request's `response.done`.
        const frames = refs.current.gate.toolResultReady(callId, resultText, Date.now());
        for (const frame of frames) {
          dc.send(JSON.stringify(frame));
        }
      } catch (err) {
        console.warn('[voice-call] failed to forward tool result:', err);
      }
    },
    [queryClient],
  );

  const handleEvent = useCallback(
    (raw: unknown) => {
      // FRONT-1469: feed every inbound event to the turn gate first, before
      // any routing/side effects, so it can track response.created/done and
      // fire a queued `response.create` the moment it's safe — independent
      // of whatever action `routeVoiceEvent` derives from the same event.
      // Known, accepted edge: `observe` may already have cleared its
      // internal `pendingCreate` flag by the time we check `dc.readyState`
      // here, so if the channel were ever NOT open at this point the frame
      // would be silently dropped rather than re-queued. That's fine in
      // practice — this handler only ever runs from `dc.onmessage`, which
      // by construction cannot fire unless the channel is open.
      // FRONT-2509: forward a finished response's usage to the cost ledger.
      // Fire-and-forget: a lost report only under-counts spend, it never
      // touches the call.
      const usageCallId = refs.current.usageCallId;
      const report = usageCallId ? realtimeUsageReportOf(raw) : null;
      if (usageCallId && report) {
        const { url: usageUrl, init: usageReportInit } = buildRealtimeUsageRequest(usageCallId, report);
        fetch(usageUrl, usageReportInit).catch((err: Error) => {
          console.debug('[voice-call] usage report threw', err.message);
        });
      }

      const dc = refs.current.dc;
      const frames = refs.current.gate.observe(raw, Date.now());
      if (frames.length > 0 && dc && dc.readyState === 'open') {
        try {
          for (const frame of frames) {
            dc.send(JSON.stringify(frame));
          }
        } catch (err) {
          console.warn('[voice-call] failed to send queued gate frame:', err);
        }
      }

      // FRONT-254: WebRTC playback of EDDY's audio — a screen move waits for
      // it to finish (capped, see NAVIGATE_PLAYBACK_WAIT_MS).
      const rawType = (raw as { type?: string } | null)?.type;
      if (rawType === 'output_audio_buffer.started') refs.current.speaking = true;
      else if (rawType === 'output_audio_buffer.stopped' || rawType === 'output_audio_buffer.cleared') {
        refs.current.speaking = false;
      }

      const action = routeVoiceEvent(raw);
      switch (action.kind) {
        case 'status':
          setStatus(action.status);
          return;
        case 'user-transcript':
          finishUserTurn(action.text);
          return;
        case 'assistant-delta':
          refs.current.assistantBuffer += action.delta;
          applyAssistantDelta(action.delta);
          return;
        case 'assistant-final': {
          // Prefer the buffered streamed text — `transcript.done` events
          // sometimes ship without the full transcript on the payload.
          const text = action.text || refs.current.assistantBuffer;
          refs.current.assistantBuffer = '';
          finishAssistantTurn(text);
          return;
        }
        case 'tool-call':
          void handleToolCall(action.callId, action.name, action.argumentsJson, action.responseId);
          return;
        case 'error':
          console.error('[voice-call] realtime error:', action.message);
          toast.error(`Voice error: ${action.message}`);
          setError(action.message);
          return;
        case 'ignore':
          return;
      }
    },
    [handleToolCall, finishUserTurn, finishAssistantTurn, applyAssistantDelta],
  );

  // FRONT-2001: the GPT-Live-1 connect path, reached from `start` once the
  // path-decision response says `transport: "live"`. Reuses the same mic
  // grant, audio sink, RTCPeerConnection handlers and generation/abandoned
  // guards `start` uses for realtime (final-plan.md §2-E) — duplicated here
  // rather than extracted into a shared step, because Live's signaling
  // diverges immediately after the offer (an ICE-gathering wait, then a
  // POST to Eddy's own server instead of a direct OpenAI dial-out with an
  // ephemeral secret), and `start`'s realtime path must stay byte-identical
  // to before this ticket — not something worth risking for a few shared
  // lines of WebRTC boilerplate.
  const startLive = useCallback(
    async (abandoned: () => boolean) => {
      try {
        const mic = await navigator.mediaDevices.getUserMedia({ audio: true });
        if (abandoned()) {
          mic.getTracks().forEach((t) => t.stop());
          return;
        }
        refs.current.mic = mic;
        mic.getAudioTracks().forEach((t) => (t.enabled = true));
        setMuted(false);
        setReconnecting(false);

        const audio = new Audio();
        audio.autoplay = true;
        audio.style.display = 'none';
        document.body.appendChild(audio);
        refs.current.audio = audio;

        const pc = new RTCPeerConnection();
        refs.current.pc = pc;
        pc.ontrack = (e) => {
          const a = refs.current.audio;
          if (!a) return;
          a.srcObject = e.streams[0] ?? null;
          a.play().catch((err) => {
            console.warn('[voice-call] audio.play() failed:', err);
          });
        };
        pc.onconnectionstatechange = () => {
          // stop() hands the refs to the next call up to 1.5s before this
          // connection is closed: a state change from this old connection
          // must never tear down the call that came after it.
          if (refs.current.pc !== pc) return;
          // FRONT-254: same graceful end as the realtime branch, once live.
          const drop = handsFreeDropAction(pc.connectionState, refs.current.handsFree && !!refs.current.liveTracker);
          if (drop === 'wait') {
            setReconnecting(true);
            return;
          }
          setReconnecting(false);
          if (drop === 'end') {
            endHandsFreeCall(stopRef.current);
            return;
          }
          if (pc.connectionState === 'failed' || pc.connectionState === 'disconnected') {
            setError(`WebRTC ${pc.connectionState}`);
            setState('error');
            cleanup();
          } else if (pc.connectionState === 'connected') {
            setState('live');
          }
        };
        mic.getTracks().forEach((track) => pc.addTrack(track, mic));

        // R2-4: the data channel is created here (WebRTC negotiation needs
        // it to exist before the offer is generated), but `onmessage` is
        // NOT attached yet — the tracker it needs doesn't exist until the
        // connect round-trip below finishes. It's attached further down,
        // right after the tracker is created, closing over that exact
        // tracker as a true local `const` (see the note there for why that
        // matters).
        const dc = pc.createDataChannel('oai-events');
        refs.current.dc = dc;

        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);
        if (abandoned()) {
          cleanup();
          return;
        }

        // Live's connect route relays the offer to OpenAI's REST create
        // endpoint rather than a streaming dial-out, so it needs the full,
        // ICE-complete offer up front.
        await waitForIceGatheringComplete(pc, ICE_GATHERING_TIMEOUT_MS);
        if (abandoned()) {
          cleanup();
          return;
        }

        const {
          sessionId: mintSid,
          branchId: mintBid,
          assetContext: mintAsset,
          scopeEntityId: mintScope,
        } = sessionInfoRef.current;
        const sdpToSend = pc.localDescription?.sdp ?? offer.sdp ?? '';
        const liveRes = await fetch('/api/voice/live/session', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...timezoneHeader() },
          // Byte-exact, never trimmed: the server passes this straight
          // through to OpenAI, whose SDP parser rejects an offer missing
          // its trailing CRLF (measured against the real API — see
          // src/voice/live.ts's own comment on the same rule).
          body: JSON.stringify({
            sdp: sdpToSend,
            sessionId: mintSid,
            branchId: mintBid,
            assetContext: mintAsset,
            scopeEntityId: mintScope,
            ...(cbRefs.current.handsFree ? { handsFree: true } : {}),
          }),
        });
        if (!liveRes.ok) {
          const detail = await liveRes.text().catch(() => '');
          throw new Error(`Live session mint failed: ${liveRes.status} ${detail.slice(0, 200)}`);
        }
        const liveData = (await liveRes.json()) as {
          sdp?: string;
          liveSessionId?: string;
          model?: string;
          handsFree?: boolean;
        };
        if (!liveData.sdp || !liveData.liveSessionId) {
          throw new Error('Live session response missing sdp or liveSessionId');
        }
        if (abandoned()) {
          cleanup();
          return;
        }
        await pc.setRemoteDescription({ type: 'answer', sdp: liveData.sdp });
        if (abandoned()) {
          cleanup();
          return;
        }

        // R2-4: a true local `const`, assigned exactly once, right here —
        // `onmessage` and the tick interval below both close over THIS
        // binding, never `refs.current.liveTracker`. Reading the ref fresh
        // on every message (the pre-fix shape) meant that for up to the
        // 1.5s `stop()` leaves an old data channel open waiting for its
        // close ack, that channel's own `onmessage` — attached before
        // `refs.current.liveTracker` existed, so it HAD to re-read the ref
        // — could pick up whatever tracker a brand-new call had since
        // assigned to that same ref and feed the old call's events into it.
        const tracker = new LiveCallTracker(liveData.liveSessionId);
        refs.current.liveTracker = tracker;
        // FRONT-254: hands-free only when the server minted it so AND the
        // host asked for it.
        refs.current.handsFree = mintedHandsFree(liveData) && !!cbRefs.current.handsFree;

        dc.onmessage = (evt) => {
          let raw: unknown;
          try {
            raw = JSON.parse(evt.data);
          } catch {
            return;
          }
          // Our own requested close resolving (see `stop`'s live branch) —
          // handled directly, independent of the tracker, since `stop()`
          // already called `tracker.stop()` by the time this can arrive and
          // the tracker would otherwise just swallow it.
          if ((raw as { type?: string })?.type === 'session.closed' && pendingLiveCloseRef.current) {
            const resolve = pendingLiveCloseRef.current;
            pendingLiveCloseRef.current = null;
            resolve();
            return;
          }
          // Diagnosability: every Live data-channel event except the two
          // transcript-delta types (see `shouldLogLiveEvent`, R2-5) — types/
          // ids only, never the transcript or delegated content itself.
          const eventType = (raw as { type?: string })?.type;
          if (shouldLogLiveEvent(eventType)) {
            console.debug('[voice-live] event', eventType);
          }
          // Review S5 / R2-4: `tracker` and `dc` are both the exact bindings
          // this closure captured when the data channel was created and the
          // tracker was built — never a fresh `refs.current` read, which by
          // the time an effect this produces (a delegation fetch) resolves,
          // or by the time a stray message from an OLD channel arrives, may
          // already belong to a different call.
          applyLiveEffectsRef.current(tracker, dc, tracker.observe(raw, Date.now()));
        };

        refs.current.liveTickHandle = setInterval(() => {
          // R2-4: same local `tracker`/`dc` the `onmessage` handler above
          // closes over — never `refs.current`.
          applyLiveEffectsRef.current(tracker, dc, tracker.tick(Date.now()));
        }, LIVE_TICK_MS);
      } catch (err) {
        console.error('[voice-call] live start failed:', err);
        const message = (err as Error).message;
        setError(message);
        toast.error(`Couldn't start voice call: ${message}`);
        setState('error');
        cleanup();
      }
    },
    [cleanup],
  );

  const start = useCallback(async () => {
    if (state === 'connecting' || state === 'live') return;
    // Reset turn-tracker + transcript so each call starts fresh.
    hadTurnRef.current = false;
    turnsRef.current = [];
    setError(null);
    setState('connecting');
    setStatus('listening');
    // FRONT-2063 fix: a new call always starts unmuted, even if the previous
    // call ended muted (e.g. a dropped connection right after muting) — mic
    // grant below resets it again once live, but the UI must not show
    // "Unmute"/muted styling while connecting.
    setMuted(false);
    setReconnecting(false);

    // Claim this call. `stop()` bumps the generation, so if the user hangs up
    // while we're still connecting, every `await` below notices it is stale
    // and tears down what it just built. Without this, `cleanup()` swaps in a
    // fresh refs object and the still-running continuation writes the mic, the
    // hidden <audio> and the peer connection into THAT one — leaving an
    // established call with a live microphone and no UI, still being billed,
    // while the surface thinks the call ended.
    const generation = ++callGenerationRef.current;
    const abandoned = () => generation !== callGenerationRef.current;

    try {
      // 1. Mint ephemeral token. Refetch right before mic grant so an
      // expired token (60s TTL) doesn't catch us if the user lingers on
      // the permission dialog.
      // FRONT-570: POST the launching chat so the session is seeded with its
      // conversation so far (loaded server-side from the session id) and its
      // anchored file/document content — the voice agent picks up where the
      // typed chat left off, as the same Eddy.
      const {
        sessionId: mintSid,
        branchId: mintBid,
        assetContext: mintAsset,
        scopeEntityId: mintScope,
      } = sessionInfoRef.current;
      const sessionRes = await fetch('/api/voice/realtime/session', {
        method: 'POST',
        // FRONT-1507: the reader's own device timezone, so the call is
        // seeded with local-time context from the start.
        headers: { 'Content-Type': 'application/json', ...timezoneHeader() },
        body: JSON.stringify({
          sessionId: mintSid,
          branchId: mintBid,
          assetContext: mintAsset,
          scopeEntityId: mintScope,
          // FRONT-254: only a host that can act on screen notes and screen
          // moves asks for them; the server still checks the flag.
          ...(cbRefs.current.handsFree ? { handsFree: true } : {}),
          // FRONT-3038: this hook handles the Live hand-off below, so it says
          // so. Without it the server always mints Realtime (other clients
          // can read only a mint); the server still checks the flag.
          supportsLive: true,
        }),
      });
      if (!sessionRes.ok) {
        const detail = await sessionRes.text().catch(() => '');
        throw new Error(`Session mint failed: ${sessionRes.status} ${detail.slice(0, 200)}`);
      }
      const sessionData = (await sessionRes.json()) as EphemeralSession & LivePathResponse;
      // FRONT-2001: the path decision (src/voice/realtime.ts) hands back
      // `{transport:"live", model}` instead of an ephemeral secret when the
      // call's model is a Live model (a saved pick, or the FRONT-254 default),
      // the flag is on and the request opted in (FRONT-3038) — everything
      // below this branch is the pre-existing realtime mint path, untouched.
      if (isLiveTransportResponse(sessionData)) {
        await startLive(abandoned);
        return;
      }
      const ephemeral = pickEphemeralValue(sessionData);
      if (!ephemeral) {
        throw new Error('Session response missing client_secret.value');
      }
      const usageCallId = usageCallIdOf(sessionData);
      // Hung up during the mint — nothing built yet, so just walk away,
      // closing the call's cost anchor right away (FRONT-2509) so it is
      // never left open to be measured against some later hang-up.
      if (abandoned()) {
        if (usageCallId) sendRealtimeHangup(usageCallId, false);
        return;
      }
      refs.current.usageCallId = usageCallId;
      // FRONT-254: see `mintedHandsFree` — the server's word, never ours alone.
      refs.current.handsFree = mintedHandsFree(sessionData) && !!cbRefs.current.handsFree;

      // 2. Mic. Default constraints — browser AEC/NS/AGC are doing real
      // work here; don't disable them.
      const mic = await navigator.mediaDevices.getUserMedia({ audio: true });
      // Hung up while the permission prompt was up: release the mic we were
      // just granted rather than leaving the recording indicator lit.
      if (abandoned()) {
        mic.getTracks().forEach((t) => t.stop());
        return;
      }
      refs.current.mic = mic;
      // A new call always starts unmuted, whatever the last one ended as.
      mic.getAudioTracks().forEach((t) => (t.enabled = true));
      setMuted(false);
      setReconnecting(false);

      // 3. Audio sink. A hidden <audio> with srcObject set to the remote
      // stream is the simplest path that respects autoplay rules across
      // browsers (the user-gesture from clicking the phone button covers
      // the play() that follows).
      const audio = new Audio();
      audio.autoplay = true;
      audio.style.display = 'none';
      document.body.appendChild(audio);
      refs.current.audio = audio;

      // 4. Peer connection.
      const pc = new RTCPeerConnection();
      refs.current.pc = pc;

      pc.ontrack = (e) => {
        const a = refs.current.audio;
        if (!a) return;
        a.srcObject = e.streams[0] ?? null;
        a.play().catch((err) => {
          // Autoplay can still fail in obscure permission states; surface
          // it loud so the user knows to check their browser settings
          // rather than wonder why Eddy is silent.
          console.warn('[voice-call] audio.play() failed:', err);
        });
      };

      pc.onconnectionstatechange = () => {
        // FRONT-254: a hands-free call that drops after it was live (the
        // 60-minute session limit, a lost network) ends through the same
        // path a hang-up takes, so the recap and save card still land.
        const liveHandsFree = refs.current.handsFree && refs.current.pc === pc && refs.current.usageConnected;
        const drop = handsFreeDropAction(pc.connectionState, liveHandsFree);
        if (drop === 'wait') {
          setReconnecting(true);
          return;
        }
        // Only this call's own connection may clear its indicator — a late
        // state change from an earlier call's connection lands here too.
        if (refs.current.pc === pc) setReconnecting(false);
        if (drop === 'end') {
          endHandsFreeCall(stopRef.current);
          return;
        }
        if (pc.connectionState === 'failed' || pc.connectionState === 'disconnected') {
          setError(`WebRTC ${pc.connectionState}`);
          setState('error');
          cleanup();
        } else if (pc.connectionState === 'connected') {
          setState('live');
          // FRONT-2509: from here on the call is billed.
          refs.current.usageConnected = true;
        }
      };

      mic.getTracks().forEach((track) => pc.addTrack(track, mic));

      // 5. Data channel. OpenAI's GA flow expects the client to open the
      // channel before the SDP exchange so the response includes its
      // a=mid lines.
      const dc = pc.createDataChannel('oai-events');
      refs.current.dc = dc;
      dc.onmessage = (evt) => {
        try {
          handleEvent(JSON.parse(evt.data));
        } catch {
          // Non-JSON frames are ignorable.
        }
      };

      // 6. SDP exchange.
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      // Hung up mid-handshake: tear down before we ever reach OpenAI, so no
      // call is established and nothing is billed.
      if (abandoned()) {
        cleanup();
        return;
      }

      const modelName = sessionData.session?.model ?? '';
      const sdpRes = await fetch(
        `${resolveRealtimeCallsUrl(sessionData.callsUrl)}?model=${encodeURIComponent(modelName)}`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${ephemeral}`,
            'Content-Type': 'application/sdp',
          },
          body: offer.sdp ?? '',
        },
      );
      if (!sdpRes.ok) {
        const detail = await sdpRes.text().catch(() => '');
        throw new Error(`SDP exchange failed: ${sdpRes.status} ${detail.slice(0, 200)}`);
      }
      const answerSdp = await sdpRes.text();
      // Last gate: a call IS established at this point, so hanging up here has
      // to hang up for real rather than orphan it.
      if (abandoned()) {
        cleanup();
        return;
      }
      await pc.setRemoteDescription({ type: 'answer', sdp: answerSdp });
      if (abandoned()) cleanup();

      // setState('live') is set by onconnectionstatechange once the peer
      // is actually connected. Until then we stay in 'connecting'.
    } catch (err) {
      console.error('[voice-call] start failed:', err);
      const message = (err as Error).message;
      setError(message);
      toast.error(`Couldn't start voice call: ${message}`);
      setState('error');
      cleanup();
    }
  }, [state, cleanup, handleEvent, startLive]);

  const stop = useCallback(() => {
    if (state === 'idle' || state === 'ending') return;
    // Invalidate any still-connecting call before tearing down, so its
    // continuation cannot rebuild what cleanup() is about to throw away.
    callGenerationRef.current += 1;
    setState('ending');

    const tracker = refs.current.liveTracker;
    if (tracker) {
      // FRONT-2001 (final-plan.md §2-F/§3 item 3): flush the tracker so any
      // still-open buffer becomes a final turn BEFORE the turns snapshot
      // below, tell the server we're hanging up, stop the mic immediately,
      // and let the server's own close ack — or a 1.5s timeout — decide
      // when the peer connection itself comes down. `refs.current` is reset
      // right away (not after the pc actually closes) so a new call can
      // start without fighting over the refs slot; the old `pc`/`dc`/`audio`
      // are captured into a local closure instead, so the delayed teardown
      // can never touch a call that started in the meantime.
      const r = refs.current;
      const dc = r.dc;
      applyLiveEffects(tracker, dc, tracker.flush(Date.now()));
      // FRONT-2001 round 3 (§7): tell the server this call is over — see
      // `notifyLiveEnded`'s own doc comment for why this is fire-and-forget
      // and guarded per-tracker rather than per-call.
      notifyLiveEnded(tracker);
      tracker.stop();
      if (r.liveTickHandle) clearInterval(r.liveTickHandle);
      r.mic?.getTracks().forEach((t) => t.stop());
      const pc = r.pc;
      const audio = r.audio;
      const closeNow = () => {
        try { pc?.getSenders().forEach((s) => s.track?.stop()); } catch { /* ignore */ }
        try { dc?.close(); } catch { /* ignore */ }
        try { pc?.close(); } catch { /* ignore */ }
        if (audio) {
          try { audio.pause(); } catch { /* ignore */ }
          audio.srcObject = null;
          audio.remove();
        }
      };
      pendingLiveCloseRef.current = closeNow;
      sendLiveFrame(dc, { type: 'session.close', event_id: `live_${crypto.randomUUID()}` });
      setTimeout(() => {
        if (pendingLiveCloseRef.current === closeNow) {
          pendingLiveCloseRef.current = null;
          closeNow();
        }
      }, 1_500);
      refs.current = createEmptyRefs();
    } else {
      cleanup();
    }

    setState('idle');
    setStatus('listening');
    setMuted(false);
    setReconnecting(false);
    // After the call ends, refetch the chat history so the persisted voice
    // turns replace the transient in-memory bubbles built during the call.
    // (Match the keys useAgentChat invalidates after a text stream ends.)
    const { sessionId: sid, branchId: bid } = sessionInfoRef.current;
    queryClient.invalidateQueries({ queryKey: chatKeys.history(sid, bid) });
    queryClient.invalidateQueries({ queryKey: ['chat', 'branches', sid] });
    queryClient.invalidateQueries({ queryKey: ['chat', 'messages-graph', sid] });
    // Fire the post-call callback only when at least one turn was exchanged —
    // so zero-turn hang-ups (e.g. connection error or immediate stop) don't
    // trigger an unnecessary recap nudge.
    const hadTurn = hadTurnRef.current;
    const turns = [...turnsRef.current];
    console.info('[voice-call] stop()', { hadTurn, turns: turns.length });
    if (hadTurn) {
      // FRONT-581: do NOT confirm anything here. The recap is produced
      // server-side by onCallEnded's /ended call, which can fail or find
      // nothing to save. The surface's handler owns the user-facing signal
      // and only claims success once the server actually persisted the recap —
      // an optimistic toast here is exactly what masked the broken path.
      cbRefs.current.onCallEnded?.(turns);
    }
  }, [state, cleanup, queryClient, applyLiveEffects, sendLiveFrame, notifyLiveEnded]);
  stopRef.current = stop;

  /**
   * Mute/unmute the microphone mid-call. Flips `enabled` on the local audio
   * tracks rather than tearing the track down, so the peer connection, the
   * call timer and the assistant's own audio all keep running — the far side
   * just stops hearing anything. A no-op when no call is up.
   *
   * FRONT-2001: a live call additionally sends the mute/unmute frame — the
   * Live protocol has no server-side VAD fallback the way realtime does, so
   * the model needs to be told explicitly. Realtime's behavior is otherwise
   * unchanged (liveTracker is always null there).
   */
  const toggleMute = useCallback(() => {
    const tracks = refs.current.mic?.getAudioTracks() ?? [];
    if (tracks.length === 0) return;
    const next = !tracks[0].enabled;
    tracks.forEach((t) => (t.enabled = next));
    setMuted(!next);
    const tracker = refs.current.liveTracker;
    if (tracker) {
      const dc = refs.current.dc;
      applyLiveEffectsRef.current(tracker, dc, next ? tracker.requestUnmute() : tracker.requestMute());
    }
  }, []);

  /**
   * FRONT-254: tell EDDY what is on screen. A no-op unless this call was
   * minted hands-free and its data channel is open — so a flag-off call can
   * never carry one.
   */
  const sendScreenNote = useCallback(
    (note: string) => {
      const r = refs.current;
      if (!r.handsFree || !r.dc || r.dc.readyState !== 'open') return false;
      const frame = r.liveTracker
        ? liveScreenNoteFrame(note, `ctx_${crypto.randomUUID()}`)
        : realtimeScreenNoteFrame(note);
      try {
        r.dc.send(JSON.stringify(frame));
        return true;
      } catch (err) {
        console.warn('[voice-call] failed to send screen note:', err);
        return false;
      }
    },
    [],
  );

  // `sessionId` is the chat this call actually lands in (the surface's own
  // thread, else the global session) — what a host watching for a late voice
  // result must key on, never its own raw prop.
  return { state, status, error, muted, reconnecting, toggleMute, start, stop, sessionId, sendScreenNote };
}
